// List of mini courses with a "new mini course" form. Click a row to
// drill into the mini course detail page (which manages the contained
// conversations).

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMiniCourses, createMiniCourse } from '../adminApi';
import { Loading, Err } from './Overview';

export default function MiniCourses() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setData(null);
            setErr(null);
            try {
                const d = await getMiniCourses();
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load');
            }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    const onCreated = () => setReloadKey((k) => k + 1);

    if (err) return <Err msg={err} />;

    return (
        <div>
            <h2 style={styles.h2}>Mini courses</h2>
            <p style={styles.intro}>
                Mini courses live outside the chapter/lecture grid. Each
                contains its own ordered set of conversations.
            </p>

            <NewMiniCourseForm onCreated={onCreated} />

            {!data ? <Loading /> : (
                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <Th>Title</Th>
                                <Th>Description</Th>
                                <Th align="right">Conversations</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.length === 0 ? (
                                <tr>
                                    <Td colSpan={3} style={{ textAlign: 'center', color: '#888' }}>
                                        No mini courses yet.
                                    </Td>
                                </tr>
                            ) : data.items.map((mc) => (
                                <tr key={mc.id}>
                                    <Td>
                                        <Link to={`/admin/mini-courses/${mc.id}`} style={styles.link}>
                                            {mc.title || `#${mc.id}`}
                                        </Link>
                                    </Td>
                                    <Td style={{ color: '#666' }}>{mc.description || '—'}</Td>
                                    <Td align="right">{mc.n_conversations}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function NewMiniCourseForm({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => {
        setTitle('');
        setDescription('');
        setError(null);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            setError('Title is required');
            return;
        }
        try {
            setSaving(true);
            await createMiniCourse({ title: title.trim(), description: description.trim() || null });
            reset();
            setOpen(false);
            onCreated && onCreated();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Create failed';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <div style={{ marginBottom: 12 }}>
                <button type="button" style={styles.btnPrimary} onClick={() => setOpen(true)}>
                    + New mini course
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} style={styles.newForm}>
            <div style={styles.newFormRow}>
                <input
                    style={styles.input}
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={saving}
                    autoFocus
                />
            </div>
            <div style={styles.newFormRow}>
                <textarea
                    style={styles.textarea}
                    placeholder="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    disabled={saving}
                />
            </div>
            {error ? <div style={styles.err}>{error}</div> : null}
            <div style={styles.newFormRow}>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? 'Creating…' : 'Create'}
                </button>
                <button
                    type="button"
                    style={styles.btnLink}
                    onClick={() => { reset(); setOpen(false); }}
                    disabled={saving}
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

function Th({ children, align }) {
    return <th style={{ ...styles.th, textAlign: align || 'left' }}>{children}</th>;
}
function Td({ children, align, style, colSpan }) {
    return <td colSpan={colSpan} style={{ ...styles.td, textAlign: align || 'left', ...(style || {}) }}>{children}</td>;
}

const styles = {
    h2: { marginTop: 0, color: '#222' },
    intro: { color: '#666', fontSize: 13, marginTop: -6, marginBottom: 14, lineHeight: 1.5 },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#888', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 },
    td: { padding: '10px 12px', borderBottom: '1px solid #f3f3f3', color: '#222' },
    link: { color: '#d97447', textDecoration: 'none', fontWeight: 600 },
    btnPrimary: { padding: '8px 14px', borderRadius: 6, border: '1px solid #d97447', background: '#d97447', color: '#fff', fontWeight: 600, cursor: 'pointer' },
    btnLink: { padding: '8px 0', border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', marginLeft: 8 },
    newForm: { padding: 16, backgroundColor: '#fff', borderRadius: 12, marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    newFormRow: { display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' },
    input: { flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 },
    textarea: { flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' },
    err: { color: '#c0392b', fontSize: 12, marginBottom: 8 },
};
